import Loader from "@shared/components/Loader";
import { Tooltip } from "flowbite-react";
import { useState, useEffect, useRef } from "react";

interface MultiSelectDropdownProps<T extends Record<string, any>> {
  selected: T[];
  onChange: (selected: T[]) => void;
  fetchOptions?: (query: string) => Promise<T[]>;
  options?: T[];
  labelKey: keyof T;
  valueKey: keyof T;
  singleSelect?: boolean;
  placeholder?: string;
  triggerDirty: () => void;
  showValueKey?: boolean;
  loading?: boolean;
  disabled?: boolean;
  queryLengthCheck?: number;
  enforceCurrentUserPresence?: {
    currentUser: any;
    as: "leader" | "member";
  };
  enforceCurrentUserText?: string;
  notFoundText?: string;
  alwaysShowOptions?: boolean;
}

function MultiSelectDropdown<T extends Record<string, any>>({
  selected,
  onChange,
  fetchOptions,
  options: staticOptions,
  labelKey,
  valueKey,
  singleSelect = false,
  placeholder = "Search...",
  triggerDirty,
  showValueKey,
  loading = false,
  disabled = false,
  queryLengthCheck = 1,
  enforceCurrentUserPresence,
  enforceCurrentUserText = "The program creator must be assigned as either a leader or a member.",
  notFoundText = "No results found",
  alwaysShowOptions = false,
}: MultiSelectDropdownProps<T>) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const useStatic = !!staticOptions;

  const handleSelect = (option: T) => {
    if (singleSelect) {
      onChange([option]);
    } else {
      onChange([...selected, option]);
    }
    setQuery("");
    setOptions([]);
    setIsOpen(false);
  };

  const handleRemove = (option: T) => {
    const isProtected =
      enforceCurrentUserPresence &&
      option[valueKey] === enforceCurrentUserPresence.currentUser[valueKey];

    if (isProtected) {
      return;
    }

    onChange(selected.filter((s) => s[valueKey] !== option[valueKey]));
    setQuery("");
    setIsOpen(false);
  };

  useEffect(() => {
    if (!alwaysShowOptions && query.length < queryLengthCheck) {
      setOptions([]);
      setIsOpen(false);
      return;
    }

    if (useStatic) {
      const filtered = staticOptions?.filter(
        (opt) =>
          !selected.some((s) => s[valueKey] === opt[valueKey]) &&
          String(opt[labelKey]).toLowerCase().includes(query.toLowerCase())
      );
      setOptions(filtered);
      setIsOpen(filtered.length > 0);
      return;
    }

    if (
      !fetchOptions ||
      (!alwaysShowOptions && query.length < queryLengthCheck)
    ) {
      setOptions([]);
      setIsOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchOptions(query);
        const filtered = results.filter(
          (opt) => !selected.some((s) => s[valueKey] === opt[valueKey])
        );
        setOptions(filtered);
        setIsOpen(true);
      } catch (err) {
        console.error("fetchOptions failed:", err);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    const closeOnClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnClickOutside);
    return () => document.removeEventListener("mousedown", closeOnClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const shouldFetch = alwaysShowOptions || query.length >= queryLengthCheck;

    if (!fetchOptions || !shouldFetch || useStatic) return;

    const fetchOnOpen = async () => {
      try {
        const results = await fetchOptions(query);
        const filtered = results.filter(
          (opt) => !selected.some((s) => s[valueKey] === opt[valueKey])
        );
        setOptions(filtered);
      } catch (err) {
        console.error("fetchOptions failed:", err);
      }
    };

    fetchOnOpen();
  }, [isOpen]);

  useEffect(() => {
    if (!alwaysShowOptions || !fetchOptions) return;

    const preload = async () => {
      try {
        const results = await fetchOptions("");
        const filtered = results.filter(
          (opt) => !selected.some((s) => s[valueKey] === opt[valueKey])
        );
        setOptions(filtered);
      } catch (err) {
        console.error("initial preload failed:", err);
      }
    };

    preload();
  }, []);

  return (
    <div
      className={`w-full relative ${disabled && "bg-[#FAFAFA]"}`}
      ref={wrapperRef}
    >
      <div
        className="flex flex-wrap gap-1 p-2 border border-gray-300 rounded-md min-h-10 2xl:min-h-12 cursor-text relative"
        onClick={() => !singleSelect && setIsOpen(true)}
      >
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 ">
            <div className="relative">
              <div className="absolute right-3 top-1/2 transform translate-y-1/2">
                <Loader color="#777" />
              </div>
            </div>
          </div>
        )}
        {disabled && (
          <div className="absolute inset-0">
            <div className="relative"></div>
          </div>
        )}
        {selected.map((option, index) => {
          const isCurrentUser =
            enforceCurrentUserPresence &&
            option[valueKey] ===
              enforceCurrentUserPresence.currentUser[valueKey];

          const spanElement = (
            <span
              key={`${option[valueKey]}_${index}`}
              className="flex items-center bg-blue-100 text-blue-700 text-xs 2xl:text-sm px-2 py-1 rounded-full"
            >
              {String(option[labelKey])}

              {!isCurrentUser && !disabled && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(option);
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700"
                  type="button"
                >
                  &times;
                </button>
              )}
            </span>
          );

          return isCurrentUser && enforceCurrentUserText ? (
            <Tooltip
              key={`${option[valueKey]}_${index}`}
              content={enforceCurrentUserText}
              placement="top"
              style="dark"
              arrow={false}
            >
              {spanElement}
            </Tooltip>
          ) : (
            spanElement
          );
        })}
        {!singleSelect || selected.length === 0 ? (
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 text-sm 2xl:text-base focus:outline-none min-w-[3rem] bg-transparent"
            placeholder={selected.length === 0 ? placeholder : ""}
            onFocus={() => {
              triggerDirty();
              setIsOpen(true);

              if (useStatic && alwaysShowOptions && staticOptions) {
                const filtered = staticOptions.filter(
                  (opt) =>
                    !selected.some((s) => s[valueKey] === opt[valueKey]) &&
                    String(opt[labelKey])
                      .toLowerCase()
                      .includes(query.toLowerCase())
                );
                setOptions(filtered);
              }
            }}
            // disabled={disabled}
          />
        ) : null}
      </div>

      {!disabled &&
        isOpen &&
        (alwaysShowOptions || query.length >= queryLengthCheck) && (
          <ul className="absolute z-[11] bg-white border rounded-lg mt-1 w-full max-h-48 overflow-auto shadow-md">
            {options.length > 0
              ? options.map((option) => (
                  <li
                    key={String(option[valueKey])}
                    className="px-3 py-2 cursor-pointer  hover:bg-gray-100"
                    onClick={() => handleSelect(option)}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm 2xl:text-base">
                        {String(option[labelKey])}
                      </span>
                      {showValueKey && (
                        <span className="text-gray-500 text-[0.65rem]">
                          {String(option[valueKey])}
                        </span>
                      )}
                    </div>
                  </li>
                ))
              : query.length >= queryLengthCheck &&
                !loading && (
                  <li
                    className="px-3 py-2 text-gray-400 text-sm cursor-default select-none"
                    aria-disabled="true"
                  >
                    {notFoundText}
                  </li>
                )}
          </ul>
        )}
    </div>
  );
}

export default MultiSelectDropdown;
